import { type ReactNode } from 'react'
import { CheckCircle2, AlertTriangle, HelpCircle } from 'lucide-react'
import type { InstanceVersion } from '../types'

interface Props {
  version: InstanceVersion | null
  migrationTarget: {
    version: string
    label: string
  }
}

function Field({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  const empty = value === null || value === undefined || value === ''
  return (
    <div className="grid grid-cols-3 gap-3 py-1.5 border-b border-border/40 last:border-b-0">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`col-span-2 text-sm ${mono ? 'font-mono break-all' : ''} ${empty ? 'text-muted-foreground' : 'text-foreground'}`}>
        {empty ? '—' : value}
      </div>
    </div>
  )
}

function Card({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="bg-card border border-border rounded-lg overflow-hidden">
      <header className="flex items-center justify-between gap-3 px-4 py-3 bg-muted border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {action}
      </header>
      <div className="p-4">{children}</div>
    </section>
  )
}

export default function VersionTab({ version, migrationTarget }: Props) {
  if (!version) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <HelpCircle className="h-8 w-8 text-muted-foreground/60 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">Não foi possível ler a versão do broker.</p>
      </div>
    )
  }

  const fork = version.openclaw.is_stationzero_fork
  const onTarget = fork === false
  const migrated = version.migration.bootstrap_applied

  let summaryIcon = <HelpCircle className="h-4 w-4 text-muted-foreground" />
  let summaryText: ReactNode = 'Versão indeterminada'
  if (migrated) {
    summaryIcon = <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
    summaryText = <>Migrado para <code className="font-mono">{migrationTarget.version}</code></>
  } else if (onTarget) {
    summaryIcon = <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
    summaryText = <>Rodando target <code className="font-mono">{migrationTarget.version}</code></>
  } else if (fork === true) {
    summaryIcon = <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
    summaryText = 'Rodando legacy — precisa migrar'
  }

  const branchDisplay = version.openclaw.branch && version.openclaw.branch !== 'HEAD'
    ? version.openclaw.branch
    : null

  return (
    <div className="space-y-4">
      <section className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
        {summaryIcon}
        <div>
          <div className="text-sm font-medium text-foreground">{summaryText}</div>
          <div className="text-xs text-muted-foreground">
            target esperado: <code className="font-mono">{migrationTarget.version}</code>
            {version.migration.raw_artifacts_present && !migrated && ' · source já staged no broker'}
          </div>
        </div>
      </section>

      <Card title="OpenClaw">
        <Field label="Image config" value={version.image.configured} mono />
        <Field label="Image rodando" value={version.image.running} mono />
        <Field label="Source dir" value={version.openclaw.source_dir} mono />
        <Field label="SHA" value={version.openclaw.sha} mono />
        <Field label="Branch" value={branchDisplay} mono />
        <Field label="Origin URL" value={version.openclaw.origin_url} mono />
        <Field label="Pinned" value={version.openclaw.pinned ? 'sim' : 'não'} />
      </Card>

      <Card title="Migração (stationzero-bootstrap)">
        <Field label="Bootstrap aplicada" value={version.migration.bootstrap_applied ? 'sim' : 'não'} />
        <Field label="Manifesto em" value={version.migration.bootstrap_manifest_at} />
        <Field label="Manifesto path" value={version.migration.bootstrap_manifest_path} mono />
        <Field
          label="Raw artifacts"
          value={version.migration.raw_artifacts_present
            ? `${version.migration.raw_artifact_count} arquivos`
            : 'ausente'}
        />
      </Card>

      {version.snapshots.length > 0 && (
        <Card title={`Snapshots (${version.snapshots.length})`}>
          <table className="w-full text-sm -my-2">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left py-2 font-medium">Tag</th>
                <th className="text-left py-2 font-medium">State</th>
                <th className="text-left py-2 font-medium">openclaw_sha</th>
                <th className="text-left py-2 font-medium">Capturado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {version.snapshots.map((s) => (
                <tr key={s.tag || s.captured_at || Math.random()}>
                  <td className="py-2 font-mono text-xs">{s.tag || '—'}</td>
                  <td className="py-2 text-xs">{s.state || '—'}</td>
                  <td className="py-2 font-mono text-xs">{s.openclaw_sha?.slice(0, 12) || '—'}</td>
                  <td className="py-2 text-xs text-muted-foreground">{s.captured_at || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <details className="bg-card border border-border rounded-lg overflow-hidden">
        <summary className="cursor-pointer px-4 py-3 bg-muted border-b border-border text-sm font-semibold">
          Diagnóstico do broker
        </summary>
        <div className="p-4">
          <Field label="Fleet dir" value={version.fleet.source_dir} mono />
          <Field label="Fleet SHA" value={version.fleet.sha} mono />
          <Field label="Fleet branch" value={version.fleet.branch} mono />
          <Field label="Fleet pinned" value={version.fleet.pinned ? 'sim' : 'não'} />
        </div>
      </details>
    </div>
  )
}
